import { IconDownload, IconEdit, IconFolder, IconFolderOpen, IconTrash, IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { type FC, useState } from 'react';
import { type IContent } from '../../../../types/content';
import { Link } from 'react-router-dom';
import { useUser } from '../../../../hooks/useUser';
import { type ISubject } from '../../../../types/subject';
import { type ITopic } from '../../../../types/topic';
import { Input, Modal, Spin } from 'antd';
import { useDownloadContent, useEditFolderContent, useRemoveFolderContent } from '../../../../services/content';
import { type ApolloQueryResult } from '@apollo/client';
import clsx from 'clsx';
import { getDocumentViewerPath, isViewableDocument } from '../../../../utils/getS3FileKey';
import { openDownloadedContent } from '../../../../utils/openDownloadedContent';

interface Props {
    content: IContent[]
    folder: string
    loading: boolean
    setSelectedContent: any
    setRemoveModal: (val: boolean) => void
    openModal: (val: boolean) => void
    openIframe: (val: string) => void
    refresh: () => Promise<ApolloQueryResult<any>>
    downloading?: string | undefined
    setDownloading: (val: string | undefined) => void
}

const Folder: FC<Props> = ({ content, openModal, refresh, folder, openIframe, loading, setSelectedContent, setRemoveModal, downloading, setDownloading }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [folderName, setFolderName] = useState('');
    const { isAdmin } = useUser();

    const { editFolder, loading: editing } = useEditFolderContent();
    const { removeFolder, loading: removing } = useRemoveFolderContent();
    const { downloadContent } = useDownloadContent();

    const folderContent = content.filter(({ folder: fld, isHidden }) => (folder === fld) && !isHidden);

    const onCloseEditfolder = () => {
        setIsEditing(false);
        setFolderName('');
    };

    const onEditFolder = async () => {
        const topic = content[0].topic as ITopic;
        await editFolder({
            oldName: folder,
            newName: folderName,
            topic: topic._id as string
        });
        await refresh();
        setIsEditing(false);
    };

    const onRemoveFolder = async () => {
        const topic = content[0].topic as ITopic;
        await removeFolder({
            folder,
            topic: topic._id as string
        });
        await refresh();
        setIsRemoving(false);
    };

    const handleDownload = async (contentId: string) => {
        setDownloading(contentId);
        const response = await downloadContent(contentId);
        openDownloadedContent(response);

        setDownloading(undefined);
    };

    return (
        <div className={clsx(
            'rounded-lg border-2 transition-all duration-300 mb-3',
            'bg-white border-gray-200',
            'dark:bg-surface-dark dark:border-borders',
            'hover:shadow-md dark:hover:shadow-lg'
        )}>
            <div className={clsx(
                'flex items-center justify-between p-3 sm:p-4',
                'border-b border-gray-200 dark:border-borders'
            )}>
                <button
                    onClick={() => setIsOpen((prev) => !prev)}
                    className={clsx(
                        'flex items-center gap-2 flex-1 text-left transition-colors duration-300',
                        'hover:text-blue-600 dark:hover:text-blue-400'
                    )}
                >
                    {isOpen ? (
                        <IconFolderOpen size={20} className={clsx(
                            'text-blue-600 dark:text-blue-400'
                        )} />
                    ) : (
                        <IconFolder size={20} className={clsx(
                            'text-gray-600 dark:text-gray-400'
                        )} />
                    )}
                    <span className={clsx(
                        'font-semibold text-sm sm:text-base transition-colors duration-300',
                        'text-gray-900 dark:text-white'
                    )}>
                        {folder}
                    </span>
                    {isOpen ? (
                        <IconChevronDown size={18} className={clsx(
                            'text-gray-600 dark:text-gray-400 ml-auto'
                        )} />
                    ) : (
                        <IconChevronRight size={18} className={clsx(
                            'text-gray-600 dark:text-gray-400 ml-auto'
                        )} />
                    )}
                </button>
                {isAdmin && (
                    <div className="flex items-center gap-2 ml-2">
                        <button
                            onClick={() => setIsEditing(true)}
                            className={clsx(
                                'p-2 rounded-lg transition-all duration-300',
                                'text-green-600 hover:bg-green-50',
                                'dark:text-green-400 dark:hover:bg-green-900/20'
                            )}
                        >
                            <IconEdit size={18} strokeWidth={2} />
                        </button>
                        <button
                            onClick={() => setIsRemoving(true)}
                            className={clsx(
                                'p-2 rounded-lg transition-all duration-300',
                                'text-red-600 hover:bg-red-50',
                                'dark:text-red-400 dark:hover:bg-red-900/20'
                            )}
                        >
                            <IconTrash size={18} strokeWidth={2} />
                        </button>
                    </div>
                )}
            </div>
            {isOpen && (
                <div className="p-3 sm:p-4 space-y-2">
                    {folderContent.map((it) => (
                        <div
                            key={it.name}
                            className={clsx(
                                'flex items-center justify-between p-3 rounded-lg border transition-all duration-300',
                                'bg-gray-50 border-gray-200',
                                'dark:bg-surface-medium dark:border-borders',
                                'hover:shadow-md dark:hover:shadow-lg'
                            )}
                        >
                            <div className="flex-1 min-w-0">
                                {it.iframeCode && (
                                    <button
                                        onClick={() => openIframe(it.iframeCode!)}
                                        className={clsx(
                                            'text-sm sm:text-base font-medium transition-colors duration-300 truncate',
                                            'text-blue-600 hover:text-blue-700',
                                            'dark:text-blue-400 dark:hover:text-blue-300'
                                        )}
                                    >
                                        {it.name}
                                    </button>
                                )}
                                {(!it.iframeCode && !isViewableDocument(it.url)) && (
                                    <a
                                        href={it.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className={clsx(
                                            'text-sm sm:text-base font-medium transition-colors duration-300 truncate block',
                                            'text-blue-600 hover:text-blue-700',
                                            'dark:text-blue-400 dark:hover:text-blue-300'
                                        )}
                                    >
                                        {it.name}
                                    </a>
                                )}
                                {(!it.iframeCode && isViewableDocument(it.url)) && (
                                    <Link
                                        to={getDocumentViewerPath(it.url, it.name, Boolean(it.downloadable), it._id)}
                                        className={clsx(
                                            'text-sm sm:text-base font-medium transition-colors duration-300 truncate block',
                                            'text-blue-600 hover:text-blue-700',
                                            'dark:text-blue-400 dark:hover:text-blue-300'
                                        )}
                                    >
                                        {it.name}
                                    </Link>
                                )}
                                {it.type === 'image' && (
                                    <a
                                        href={it.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className={clsx(
                                            'text-sm sm:text-base font-medium transition-colors duration-300 truncate block',
                                            'text-blue-600 hover:text-blue-700',
                                            'dark:text-blue-400 dark:hover:text-blue-300'
                                        )}
                                    >
                                        {it.name}
                                    </a>
                                )}
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                                {it.downloadable && (
                                    <button
                                        onClick={async () => await handleDownload(it._id!)}
                                        disabled={downloading === it._id}
                                        className={clsx(
                                            'p-2 rounded-lg transition-all duration-300',
                                            'text-blue-600 hover:bg-blue-50',
                                            'dark:text-blue-400 dark:hover:bg-blue-900/20',
                                            'disabled:opacity-50 disabled:cursor-not-allowed'
                                        )}
                                    >
                                        {downloading === it._id ? (
                                            <Spin size="small" />
                                        ) : (
                                            <IconDownload size={18} strokeWidth={2} />
                                        )}
                                    </button>
                                )}
                                {isAdmin && (
                                    <>
                                        <button
                                            onClick={() => {
                                                setSelectedContent({
                                                    ...it,
                                                    subject: (it.subject as ISubject)._id!,
                                                    topic: (it.topic as ITopic)._id!
                                                });
                                                setTimeout(() => {
                                                    openModal(true);
                                                }, 500);
                                            }}
                                            className={clsx(
                                                'p-2 rounded-lg transition-all duration-300',
                                                'text-green-600 hover:bg-green-50',
                                                'dark:text-green-400 dark:hover:bg-green-900/20'
                                            )}
                                        >
                                            <IconEdit size={18} strokeWidth={2} />
                                        </button>
                                        <button
                                            onClick={() => {
                                                setRemoveModal(true);
                                                setSelectedContent(it);
                                            }}
                                            className={clsx(
                                                'p-2 rounded-lg transition-all duration-300',
                                                'text-red-600 hover:bg-red-50',
                                                'dark:text-red-400 dark:hover:bg-red-900/20'
                                            )}
                                        >
                                            <IconTrash size={18} strokeWidth={2} />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <Modal
                open={isRemoving}
                onCancel={() => setIsRemoving(false)}
                okButtonProps={{
                    danger: true,
                    loading: removing
                }}
                onOk={onRemoveFolder}
                okText="Eliminar"
                title="Eliminar carpeta"
                centered={false}
                className={clsx(
                    'remove-folder-modal',
                    'dark:text-white'
                )}
                style={{
                    borderRadius: '16px',
                    overflow: 'hidden'
                }}
                maskStyle={{
                    backdropFilter: 'blur(4px)',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)'
                }}
                wrapClassName={clsx(
                    'remove-folder-modal-wrapper',
                    'bottom-sheet-mobile'
                )}
            >
                <p className={clsx(
                    'text-gray-600 dark:text-gray-300'
                )}>
                    {'Esto solo eliminará la carpeta y dejará su contenido fuera, no borrará el contenido de la misma.'}
                </p>
            </Modal>
            <Modal
                open={isEditing}
                onCancel={onCloseEditfolder}
                okButtonProps={{
                    loading: editing
                }}
                onOk={onEditFolder}
                okText="Guardar"
                title="Editar carpeta"
                centered={false}
                className={clsx(
                    'edit-folder-modal',
                    'dark:text-white'
                )}
                style={{
                    borderRadius: '16px',
                    overflow: 'hidden'
                }}
                maskStyle={{
                    backdropFilter: 'blur(4px)',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)'
                }}
                wrapClassName={clsx(
                    'edit-folder-modal-wrapper',
                    'bottom-sheet-mobile'
                )}
            >
                <p className={clsx(
                    'text-gray-600 dark:text-gray-300 mb-4'
                )}>
                    {'Se cambiará el nombre de la carpeta. Esta acción no afectará al contenido'}
                </p>
                <Input
                    placeholder='Nombre de la carpeta'
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    className={clsx(
                        '[&_.ant-input]:bg-white',
                        '[&_.ant-input]:dark:bg-surface-dark',
                        '[&_.ant-input]:border-gray-300',
                        '[&_.ant-input]:dark:border-borders',
                        '[&_.ant-input]:text-gray-900',
                        '[&_.ant-input]:dark:text-white',
                        '[&_.ant-input]:placeholder:text-gray-400',
                        '[&_.ant-input]:dark:placeholder:text-gray-500',
                        '[&_.ant-input]:focus:border-blue-500',
                        '[&_.ant-input]:dark:focus:border-blue-400'
                    )}
                />
            </Modal>
        </div>
    );
};

export default Folder;
